import React, { useState } from "react";
import { Modal, Form, Input, Button, Select, DatePicker, Radio, Row, Col, Divider, Checkbox, Spin, Result } from "antd";
import {
  UserOutlined,
  SafetyCertificateOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  LockOutlined,
  ArrowRightOutlined,
  EditOutlined
} from "@ant-design/icons";
import moment from "moment";
import { customBaseUrl, httpPost } from "../../store/http";
import { NotificationManager } from "react-notifications";
import { showLoader, hideLoader } from "../../utils/loader";
import Title from "antd/es/typography/Title";

const { Option } = Select;

const CreateUserModal = ({ visible, onCancel, onCreate }) => {
  const [form] = Form.useForm();
  const [otpForm] = Form.useForm();
  const [accountType, setAccountType] = useState("personal");
  const [isFetchingBvn, setIsFetchingBvn] = useState(false);
  const [bvnVerified, setBvnVerified] = useState(false);

  // OTP States
  const [isOtpModalVisible, setIsOtpModalVisible] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpVerificationToken, setOtpVerificationToken] = useState(null);

  const handleVerifyBvn = async () => {
    const bvn = form.getFieldValue("bvn");
    const dateOfBirth = form.getFieldValue("dateOfBirth");
    const processType = form.getFieldValue("processType");

    if (!bvn || bvn.length !== 11) {
      NotificationManager.error("Please enter a valid 11-digit BVN.");
      return;
    }
    if (!dateOfBirth) {
      NotificationManager.error("Please select a Date of Birth.");
      return;
    }
    if (!processType) {
      NotificationManager.error("Please select a Process Type.");
      return;
    }

    setIsFetchingBvn(true);
    showLoader();
    try {
      const bvnRes = await httpPost(
        `${customBaseUrl.authUrl}/auth/bvn/verify`,
        {
          bvn,
          dateOfBirth: dateOfBirth.format("YYYY-MM-DD"),
          processType,
        },
      );

      if (bvnRes?.success) {
        setOtpVerificationToken(bvnRes.data.otpVerificationToken);
        setIsOtpModalVisible(true);
        NotificationManager.info("OTP sent to linked phone number");
      } else {
        throw new Error(bvnRes?.message || "BVN verification failed.");
      }
    } catch (error) {
      NotificationManager.error(error.message || "BVN verification failed.");
    } finally {
      setIsFetchingBvn(false);
      hideLoader();
    }
  };

  const handleVerifyOtp = async (values) => {
    setOtpLoading(true);
    try {
      const otpVerifyRes = await httpPost(
        `${customBaseUrl.authUrl}/auth/bvn/verify-otp`,
        {
          otpVerificationToken,
          otp: values.otp
        },
      );

      if (otpVerifyRes?.success) {
        const parsedDob = otpVerifyRes?.data?.dateOfBirth
          ? moment(otpVerifyRes.data.dateOfBirth, "DD-MMM-YYYY")
          : null;

        form.setFieldsValue({
          firstName: otpVerifyRes.data.firstName,
          lastName: otpVerifyRes.data.lastName,
          phoneNumber: otpVerifyRes.data.phoneNumber1,
          dateOfBirth: parsedDob,
          gender: otpVerifyRes.data.gender?.toUpperCase(),
          address: otpVerifyRes.data.residentialAddress,
          state: otpVerifyRes.data.stateOfResidence || otpVerifyRes.data.stateOfOrigin,
          city: otpVerifyRes.data.lgaOfResidence || otpVerifyRes.data.lgaOfOrigin,
          country: otpVerifyRes.data.nationality || "Nigeria",
        });
        setBvnVerified(true);
        setIsOtpModalVisible(false);
        otpForm.resetFields();
        NotificationManager.success("BVN Verified Successfully!");
      } else {
        NotificationManager.error(otpVerifyRes?.message || "OTP verification failed.");
      }
    } catch (error) {
      NotificationManager.error("OTP verification failed.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    otpForm.resetFields();
    setBvnVerified(false);
    setAccountType("personal");
    setIsOtpModalVisible(false);
    onCancel();
  };

  const onOk = () => {
    form.validateFields().then((values) => {
      // Map to the required API structure
      const formattedValues = {
        email: values.email,
        phoneNumber: values.phoneNumber,
        firstName: values.firstName,
        lastName: values.lastName,
        dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format("YYYY-MM-DD") : undefined,
        bvn: values.bvn,
        gender: values.gender,
        address: {
          street: values.address,
          city: values.city,
          state: values.state,
          country: values.country
        },
        notes: values.notes || "",
        businessName: accountType === "non-cac-business" ? values.businessName : undefined
      };
      onCreate(formattedValues, accountType);
    });
  };

  return (
    <>
      <Modal
        visible={visible}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <UserOutlined style={{ color: '#ff6700' }} />
            <span>Register New Customer</span>
          </div>
        }
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel} style={{ borderRadius: '8px' }}>Cancel</Button>,
          <Button key="submit" type="primary" onClick={onOk} style={{ borderRadius: '8px', background: '#ff6700', borderColor: '#ff6700' }}>
            Complete Registration
          </Button>
        ]}
        width={800}
        className="premium-modal"
      >
        <Form form={form} layout="vertical" className="premium-form">
          <Row gutter={24}>
            <Col span={8}>
              <Form.Item
                name="dateOfBirth"
                label="Date of Birth"
                rules={[{ required: true, message: "Required for BVN lookup" }]}
              >
                <DatePicker style={{ width: "100%" }} size="large" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="processType"
                label="Process Type"
                rules={[{ required: true, message: "Please select process type" }]}
                initialValue="BASIC"
              >
                <Select size="large">
                  <Option value="BASIC">BASIC</Option>
                  <Option value="ADVANCE">ADVANCE</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="BVN Verification" required>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Form.Item
                    name="bvn"
                    noStyle
                    rules={[{ required: true, len: 11, message: "Valid 11-digit BVN required" }]}
                  >
                    <Input
                      placeholder="11-digit BVN"
                      size="large"
                      disabled={bvnVerified}
                      prefix={<SafetyCertificateOutlined style={{ color: bvnVerified ? '#52c41a' : '#d9d9d9' }} />}
                    />
                  </Form.Item>
                  <Button
                    type="primary"
                    onClick={handleVerifyBvn}
                    loading={isFetchingBvn}
                    disabled={bvnVerified}
                    style={{ background: bvnVerified ? '#52c41a' : '#001529', borderColor: bvnVerified ? '#52c41a' : '#001529' }}
                  >
                    {bvnVerified ? <CheckCircleOutlined /> : "Verify"}
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>

          {bvnVerified && (
            <div className="verified-fields-container" style={{ animation: 'fadeIn 0.5s ease-in-out' }}>
              <Divider orientation="left" style={{ color: '#8c8c8c', fontSize: '12px' }}>CUSTOMER PROFILE</Divider>

              <Form.Item label="Registration Type" name="accountType" initialValue="personal">
                <Radio.Group onChange={(e) => setAccountType(e.target.value)} buttonStyle="solid">
                  <Radio.Button value="personal">Personal</Radio.Button>
                  {/* <Radio.Button value="non-cac-business">Non-CAC Business</Radio.Button> */}
                </Radio.Group>
              </Form.Item>

              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                    <Input size="large" disabled prefix={<UserOutlined style={{ color: '#d9d9d9' }} />} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                    <Input size="large" disabled prefix={<UserOutlined style={{ color: '#d9d9d9' }} />} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="email" label="Email Address" rules={[{ required: true, type: 'email' }]}>
                    <Input size="large" placeholder="email@example.com" prefix={<MailOutlined style={{ color: '#d9d9d9' }} />} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="phoneNumber" label="Phone Number" rules={[{ required: true }]}>
                    <Input size="large" prefix={<PhoneOutlined style={{ color: '#d9d9d9' }} />} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="gender" label="Gender" rules={[{ required: true }]}>
                    <Select size="large">
                      <Option value="MALE">Male</Option>
                      <Option value="FEMALE">Female</Option>
                    </Select>
                  </Form.Item>
                </Col>
                {accountType === "non-cac-business" && (
                  <Col span={12}>
                    <Form.Item name="businessName" label="Business Name" rules={[{ required: true }]}>
                      <Input size="large" placeholder="Registered trading name" />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              <Divider orientation="left" style={{ color: '#8c8c8c', fontSize: '12px' }}>LOCATION DETAILS</Divider>

              <Form.Item label="Residential Address" name="address" rules={[{ required: true }]}>
                <Input.TextArea rows={2} prefix={<EnvironmentOutlined />} placeholder="Full street address..." />
              </Form.Item>

              <Row gutter={24}>
                <Col span={8}>
                  <Form.Item name="city" label="City" rules={[{ required: true }]}>
                    <Input size="large" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="state" label="State" rules={[{ required: true }]}>
                    <Input size="large" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="country" label="Country" rules={[{ required: true }]} initialValue="Nigeria">
                    <Input size="large" prefix={<GlobalOutlined style={{ color: '#d9d9d9' }} />} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" style={{ color: '#8c8c8c', fontSize: '12px' }}>ADDITIONAL INFO</Divider>
              <Form.Item name="notes" label="Admin Notes">
                <Input.TextArea rows={3} placeholder="Add any relevant notes about this customer..." prefix={<EditOutlined />} />
              </Form.Item>
            </div>
          )}
        </Form>
      </Modal>

      {/* Premium OTP Modal */}
      <Modal
        visible={isOtpModalVisible}
        onCancel={() => setIsOtpModalVisible(false)}
        footer={null}
        width={400}
        centered
        className="otp-modal"
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{
            width: 64,
            height: 64,
            background: '#fff7e6',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            border: '1px solid #ffe7ba'
          }}>
            <LockOutlined style={{ fontSize: 32, color: '#ff6700' }} />
          </div>
          <Title level={4}>Two-Step Verification</Title>
          <p>
            Please enter the 6-digit code sent to the phone number linked to your BVN.
          </p>

          <Form
            form={otpForm}
            onFinish={handleVerifyOtp}
            layout="vertical"
            style={{ marginTop: 32 }}
          >
            <Form.Item
              name="otp"
              rules={[{ required: true, message: 'OTP is required' }, { len: 6, message: 'Enter 6 digits' }]}
            >
              <Input
                placeholder="0 0 0 0 0 0"
                size="large"
                style={{
                  textAlign: 'center',
                  fontSize: 24,
                  letterSpacing: 8,
                  fontWeight: 'bold',
                  borderRadius: 12,
                  height: 60
                }}
                maxLength={6}
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={otpLoading}
              block
              size="large"
              style={{
                height: 50,
                borderRadius: 12,
                background: '#ff6700',
                borderColor: '#ff6700',
                marginTop: 16
              }}
              icon={<ArrowRightOutlined />}
            >
              Verify OTP
            </Button>
            <div style={{ marginTop: 24 }}>
              <p style={{ fontSize: 13 }}>
                Didn't receive the code? <a href="#" onClick={(e) => { e.preventDefault(); handleVerifyBvn(); }} style={{ color: '#ff6700', fontWeight: 600 }}>Resend</a>
              </p>
            </div>
          </Form>
        </div>
      </Modal>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .premium-modal .ant-modal-content, .otp-modal .ant-modal-content {
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1) !important;
        }
        .premium-modal .ant-modal-header {
          padding: 24px;
          background: #fafafa;
          border-bottom: 1px solid #f0f0f0;
        }
        .premium-form .ant-form-item-label > label {
          font-weight: 600;
          color: #595959;
          font-size: 13px;
        }
        .verified-fields-container {
          margin-top: 24px;
          padding: 24px;
          background: #fff;
          border: 1px solid #e8e8e8;
          border-radius: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.02);
        }
        .otp-modal .ant-modal-body {
          padding: 40px 32px;
        }
        .otp-modal .ant-input:focus {
          border-color: #ff6700;
          box-shadow: 0 0 0 2px rgba(255, 103, 0, 0.1);
        }
        .ant-btn {
          cursor: pointer !important;
        }
        .ant-btn:disabled {
          cursor: not-allowed !important;
        }
      `}</style>
    </>
  );
};

export default CreateUserModal;
